import { mergeUint8Arrays } from '@utils/common/mergeUint8Arrays';

import { ELoadingState, INetworkError } from '../INetworkLoader';
import { INetworkLoadFetchConfig } from './INetworkLoadFetch';
import { getNetworkStats, INetworkLoadStats } from './NetworkLoadStats';

export class NetworkLoadFetch {
  public requestUrl: string = '';
  public realUrl: string = '';
  public mode: string = '';
  public error?: INetworkError;

  private _dataChunks: Uint8Array[] = [];
  private _receivedLength: number = 0;
  private _abortController: AbortController = new AbortController();
  private _startLoadTimestamp: number = 0;
  private _isAborted: boolean = false;

  private _config: INetworkLoadFetchConfig;

  constructor(config: INetworkLoadFetchConfig) {
    this._config = config;
  }

  public abort(): void {
    this._isAborted = true;
    this._abortController.abort();
  }

  public async fetch(url: string, mode: string): Promise<void> {
    try {
      if (globalThis.performance && globalThis.performance.clearResourceTimings) {
        globalThis.performance.clearResourceTimings();
      }

      this._config.onChangeState(ELoadingState.LOADING);
      this._startLoadTimestamp = Date.now();

      this.requestUrl = url;
      this.mode = mode;
      this._receivedLength = 0;

      const request: Request = new Request(this.requestUrl);

      const requestInit: RequestInit = {
        signal: this._abortController.signal,
      };

      if (this._config.requestCustomReferer) {
        requestInit.headers = {
          'X-Referer': this._config.requestCustomReferer,
        };
      }

      const response: Response = await fetch(request, requestInit);

      this.realUrl = response.url;
      const reader: ReadableStreamDefaultReader<Uint8Array> | undefined = response.body?.getReader();

      if (!reader) {
        throw new Error();
      }

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        this._dataChunks.push(value);
        this._receivedLength += value.length;
      }

      if (!response.ok && (!response.status || response.status > 399)) {
        this.error = {
          status: response.status,
          statusText: response.statusText || this._receivedLength ? String.fromCharCode(...mergeUint8Arrays(this._dataChunks)) : '',
        };
        throw new Error();
      }

      const binaryData: ArrayBuffer = mergeUint8Arrays(this._dataChunks).buffer as ArrayBuffer;

      this._config.onDataReady(this.mode !== 'text' ? binaryData : new TextDecoder('utf-8').decode(binaryData));
      this._config.onStatsReady(this._getNetworkStats());

      if (this.realUrl !== this.requestUrl) {
        this._config.onRedirectDetected(this.requestUrl, this.realUrl);
      }

      // onChangeState with LOADED state should always go last, because it leads to destroying current instance
      this._config.onChangeState(ELoadingState.LOADED, { realUrl: response.url, status: response.status });
    } catch (e: unknown) {
      if (this._isAborted) {
        this._config.onStatsReady(this._getNetworkStats());
        this._config.onChangeState(ELoadingState.ABORTED);
        return;
      }
      if (!this.error) {
        this.error = {
          status: -1,
          statusText: (e as Error).toString(),
        };
      }
      this._config.onError(this.error);

      // onChangeState with ERROR state should always go last, because it leads to destroying current instance
      this._config.onChangeState(ELoadingState.ERROR, { status: this.error.status });
    }
  }

  private _getNetworkStats(): INetworkLoadStats {
    const elapsedTime: number = Date.now() - this._startLoadTimestamp;
    const mode: 'text' | 'binary' = this.mode === 'text' ? 'text' : 'binary';

    return getNetworkStats(this.requestUrl, elapsedTime, this._receivedLength, mode);
  }
}
